import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Car, Package, MapPin, Shield, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  
  // CMS content state
  const [footerContent, setFooterContent] = useState(null);

  // Fetch CMS content
  useEffect(() => {
    const fetchCMSContent = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/cms/content`);
        setFooterContent(response.data);
      } catch (error) {
        console.error('Error fetching CMS content:', error);
      }
    };
    fetchCMSContent();
  }, []);

  // Auto-redirect logged in users to their portal (especially for PWA)
  useEffect(() => {
    // Check if this is a PWA launch or standalone mode
    const isPWA = searchParams.get('pwa') === 'true' ||
                  window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone || 
                  document.referrer.includes('android-app://');
    
    // Check for logged in driver first (priority for driver PWA)
    const driverId = localStorage.getItem('driver_id');
    if (driverId) {
      console.log('Found driver session, redirecting to driver portal');
      navigate('/driver', { replace: true });
      return;
    }
    
    // Check for logged in user
    const userId = localStorage.getItem('user_id');
    if (userId) {
      console.log('Found user session, redirecting to user portal');
      navigate('/user', { replace: true });
      return;
    }
    
    // If PWA but not logged in, we could redirect to a specific portal
    // For now, let them choose from landing page
    if (isPWA) {
      console.log('PWA launch detected but no session found');
    }
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain" />
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Services</span>
          </div>
          <div className="flex gap-2 md:gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => navigate('/user/login')} className="border-slate-700 text-slate-700 hover:bg-slate-50 text-sm md:text-base whitespace-nowrap">
              User Login
            </Button>
            <Button variant="outline" onClick={() => navigate('/driver/login')} className="border-amber-600 text-amber-700 hover:bg-amber-50 text-sm md:text-base whitespace-nowrap">
              Driver Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="mb-8">
            <img src={logoUrl} alt="Logo" className="h-24 w-24 mx-auto mb-6 object-contain" />
          </div>
          <h1 className="text-6xl font-bold bg-gradient-to-r from-slate-800 via-amber-600 to-yellow-500 bg-clip-text text-transparent leading-tight">
            Go Anywhere, Send Anything
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your premium all-in-one transportation platform. Book rides or schedule deliveries in seconds.
          </p>
          <div className="flex gap-4 justify-center pt-6">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => navigate('/user')}
            >
              <Car className="mr-2" size={20} />
              Book a Ride
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-amber-600 text-amber-700 hover:bg-amber-50 px-8 py-6 text-lg rounded-xl"
              onClick={() => navigate('/user?tab=delivery')}
            >
              <Package className="mr-2" size={20} />
              Send a Package
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-8 hover:shadow-lg transition-shadow border-2 hover:border-amber-200">
            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
              <MapPin className="text-amber-600" size={28} />
            </div>
            <h3 className="text-xl font-bold mb-2">Real-Time Tracking</h3>
            <p className="text-gray-600">Track your ride or delivery in real-time with live GPS updates.</p>
          </Card>
          <Card className="p-8 hover:shadow-lg transition-shadow border-2 hover:border-slate-200">
            <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
              <Shield className="text-slate-700" size={28} />
            </div>
            <h3 className="text-xl font-bold mb-2">Safe & Secure</h3>
            <p className="text-gray-600">All drivers are verified and background-checked for your safety.</p>
          </Card>
          <Card className="p-8 hover:shadow-lg transition-shadow border-2 hover:border-amber-200">
            <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
              <Clock className="text-yellow-600" size={28} />
            </div>
            <h3 className="text-xl font-bold mb-2">Quick & Reliable</h3>
            <p className="text-gray-600">Get picked up in minutes with our network of drivers.</p>
          </Card>
        </div>
      </section>

      {/* CTA Section for Drivers */}
      <section className="container mx-auto px-4 py-20">
        <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 text-white p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-amber-500/10 to-yellow-500/10" />
          <div className="relative z-10">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Car size={32} className="text-slate-900" />
            </div>
            <h2 className="text-4xl font-bold mb-4">Drive & Earn with J-Style Services</h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto opacity-90">
              Join thousands of drivers earning on their own schedule. Sign up in minutes and start earning today.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
                onClick={() => navigate('/drivers')}
              >
                Learn More
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="bg-white text-slate-900 hover:bg-slate-100 border-2 px-8 py-6 text-lg rounded-xl"
                onClick={() => navigate('/driver/signup')}
              >
                Apply Now
              </Button>
            </div>
          </div>
        </Card>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Section */}
            <div>
              <h4 className="font-bold mb-4 text-amber-400">
                {footerContent?.company_section?.title || 'Company'}
              </h4>
              <ul className="space-y-2 text-gray-400">
                {(footerContent?.company_section?.links || [
                  { label: 'About Us', url: '/about', is_internal: true },
                  { label: 'Careers', url: '/careers', is_internal: true },
                  { label: 'Press', url: '/press', is_internal: true }
                ]).map((link, idx) => (
                  <li key={idx}>
                    {link.is_internal ? (
                      <button onClick={() => navigate(link.url)} className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </button>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Services Section */}
            <div>
              <h4 className="font-bold mb-4 text-amber-400">
                {footerContent?.services_section?.title || 'Services'}
              </h4>
              <ul className="space-y-2 text-gray-400">
                {(footerContent?.services_section?.links || [
                  { label: 'Ride', url: '/user', is_internal: true },
                  { label: 'Delivery', url: '/delivery-app', is_internal: true },
                  { label: 'Business', url: '/user?tab=delivery', is_internal: true }
                ]).map((link, idx) => (
                  <li key={idx}>
                    {link.is_internal ? (
                      <button onClick={() => navigate(link.url)} className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </button>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Support Section */}
            <div>
              <h4 className="font-bold mb-4 text-amber-400">
                {footerContent?.support_section?.title || 'Support'}
              </h4>
              <ul className="space-y-2 text-gray-400">
                {(footerContent?.support_section?.links || [
                  { label: 'Help Center', url: '/help', is_internal: true },
                  { label: 'Safety', url: '/safety', is_internal: true },
                  { label: 'Contact', url: '/contact', is_internal: true }
                ]).map((link, idx) => (
                  <li key={idx}>
                    {link.is_internal ? (
                      <button onClick={() => navigate(link.url)} className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </button>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Resources Section */}
            <div>
              <h4 className="font-bold mb-4 text-amber-400">
                {footerContent?.resources_section?.title || 'Resources'}
              </h4>
              <ul className="space-y-2 text-gray-400">
                {(footerContent?.resources_section?.links || [
                  { label: 'Shareable Links', url: '/links', is_internal: true },
                  { label: 'Privacy', url: '/privacy', is_internal: true },
                  { label: 'Terms', url: '/terms', is_internal: true }
                ]).map((link, idx) => (
                  <li key={idx}>
                    {link.is_internal ? (
                      <button onClick={() => navigate(link.url)} className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </button>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400">
            <p>{footerContent?.copyright_text || '© 2025 J-Style Services. All rights reserved.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;